import type { Ctx } from '../../ctx';
import { driftFor, fmt } from '../../model';
import { CutoverList, StepFlow, Switch } from '../../ui';
import type { Pipeline, WebhookEntry } from '../../types';

export default function PipelineTab({
  ctx,
  entry,
  pipeline,
}: {
  ctx: Ctx;
  entry: WebhookEntry;
  pipeline: Pipeline;
}) {
  const key = pipeline.key;
  const dial = ctx.data.dials?.[key];
  const intent = ctx.data.intent?.[key] ?? ctx.data.service?.monitor.intent?.[key];
  const drift = driftFor(key, dial, intent);
  const change = ctx.data.dialChanges?.[key] ?? null;
  const locked = !ctx.admin || !!pipeline.parked || !dial;
  const lockTitle = !ctx.admin ? 'admin only' : pipeline.parked ? 'parked' : undefined;

  return (
    <>
      {drift.length ? (
        <div className="notice" style={{ marginBottom: 14 }}>
          <strong>dial_drift:</strong> {drift.join(', ')} — the monitor posts this on the next tick.
          If intended, change <code>MON_INTENT</code> in <code>tools/emit-k8s-secrets.sh</code>.
        </div>
      ) : null}

      <section className="grid g2">
        <div className="card pad">
          <h2>Dial</h2>
          <div
            className="row"
            style={{
              justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            <span>
              <strong>Enabled</strong>
              <div className="note">
                {pipeline.parked ? (
                  'Parked — another team owns this flow; leave off.'
                ) : (
                  <>
                    Off = webhooks acknowledged (200) and recorded as <code>skipped:disabled</code>,
                    nothing processed.
                  </>
                )}
              </div>
            </span>
            <Switch
              on={!!dial?.enabled}
              disabled={locked}
              title={lockTitle}
              label="enabled"
              onToggle={() => ctx.requestDial(key, 'enabled', !dial?.enabled)}
            />
          </div>
          <div className="row" style={{ justifyContent: 'space-between', padding: '8px 0' }}>
            <span>
              <strong>Dry-run</strong>
              <div className="note">
                {dial?.dryRun ? (
                  <>
                    Shadow: every step computes and records <code>would</code>; nothing is written to
                    HubSpot / Customer.io.
                  </>
                ) : (
                  'LIVE: steps write to HubSpot / Customer.io.'
                )}
              </div>
            </span>
            <Switch
              on={!!dial?.dryRun}
              warn
              disabled={locked}
              title={lockTitle}
              label="dryRun"
              onToggle={() => ctx.requestDial(key, 'dryRun', !dial?.dryRun)}
            />
          </div>
          <dl className="kv" style={{ marginTop: 10 }}>
            <dt>Intent</dt>
            <dd>
              {intent && Object.keys(intent).length ? (
                Object.entries(intent).map(([f, v]) => (
                  <code key={f}>
                    {f}={String(v)}{' '}
                  </code>
                ))
              ) : (
                <span className="faint">none declared</span>
              )}{' '}
              <span className="note">
                (<code>RW_MON_INTENT</code>, checked by dial_drift)
              </span>
            </dd>
            <dt>Last change</dt>
            <dd>
              {change ? (
                <>
                  {change.by ?? 'unknown'} <span className="dim">· {fmt(change.at)}</span>
                </>
              ) : (
                <span className="faint">no dial row yet</span>
              )}{' '}
              <span className="note">
                (<code>dials.updated_by</code>; every set is also in Tools → Recent actions)
              </span>
            </dd>
            <dt>API</dt>
            <dd>
              <code>PUT /api/dials {`{"${key}":{…}}`}</code> (admin)
            </dd>
          </dl>
        </div>

        <div className="card pad">
          <h2>What this pipeline does</h2>
          <p className="note" style={{ margin: '0 0 10px' }}>
            {pipeline.description}
          </p>
          <StepFlow pipeline={pipeline} />
          <p className="note" style={{ margin: '6px 0 0' }}>
            Every step is idempotent through the ledger: a retry skips steps that already read{' '}
            <code>ok</code>.
          </p>
        </div>
      </section>

      {key === 'tts' && ctx.env === 'prod' ? (
        <section className="card pad">
          <h2>Cutover checklist (prod → LIVE)</h2>
          <CutoverList state={ctx.cutover} onChange={ctx.setCut} />
          <p className="note" style={{ marginTop: 8 }}>
            All boxes ticked ⇒ the Dry-run toggle above unlocks.
          </p>
        </section>
      ) : null}

      {entry.registered === false ? (
        <p className="note">
          This route exists but holds no registration on {entry.source}; nothing will arrive until
          one is created.
        </p>
      ) : null}
    </>
  );
}
